/**
 * Public client appointment cancellation API (token link from confirmation email).
 * Sends client JWT when present (OTP skipped for appointment owner).
 */
import { httpClient } from './http';

export interface CancelEligibility {
  eligible: boolean;
  reason?: string;
  deadlineHours: number;
  hoursUntilAppointment: number;
  refundEligible: boolean;
  cancelDeadlineAt: string | null;
}

export interface CancelAppointmentSummary {
  appointmentId: string;
  scheduledAt: string;
  appointmentDateFormatted: string;
  shopName: string;
  serviceName: string;
  clientFirstName: string;
  eligibility: CancelEligibility;
  otpRequired: boolean;
}

type ApiError = { status?: number; data?: { success?: boolean; message?: string; reason?: string } };

export const appointmentCancelApi = {
  async getSummary(token: string): Promise<{
    success: boolean;
    data?: CancelAppointmentSummary;
    message?: string;
    reason?: string;
  }> {
    try {
      return await httpClient.get(`/api/public/appointments/cancel/${encodeURIComponent(token)}`, true);
    } catch (e) {
      const err = e as ApiError;
      return {
        success: false,
        message: err.data?.message || 'Impossible de charger le rendez-vous',
        reason: err.data?.reason,
      };
    }
  },

  async sendOtp(token: string): Promise<{
    success: boolean;
    message?: string;
    otpRequired?: boolean;
    otpValidityMinutes?: number;
  }> {
    try {
      return await httpClient.post(
        `/api/public/appointments/cancel/${encodeURIComponent(token)}/otp`,
        {},
        true,
      );
    } catch (e) {
      const err = e as ApiError;
      return { success: false, message: err.data?.message || "Échec d'envoi du code" };
    }
  },

  async confirm(
    token: string,
    body: { reason: string; otp?: string },
  ): Promise<{
    success: boolean;
    message?: string;
    data?: { appointmentId: string; refundEligible: boolean };
  }> {
    try {
      return await httpClient.post(
        `/api/public/appointments/cancel/${encodeURIComponent(token)}/confirm`,
        body,
        true,
      );
    } catch (e) {
      const err = e as ApiError;
      return { success: false, message: err.data?.message || "Échec de l'annulation" };
    }
  },
};
